import { memo, useMemo } from 'react';

import Divider from '@zenbusiness/zui/Divider';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import PaymentRow from './PaymentRow';

function ListPaymentTransaction({
  title,
  transactions,
  isLoading = false,
  hasMore = false,
  handleLoadMore,
  refetchPayment,
  counterparty
}) {
  const paymentRows = useMemo(
    () =>
      transactions.map(
        ({
          id,
          createdAt,
          summary,
          direction,
          amount,
          type,
          status,
          estimatedCompletionDate,
          transactionId,
          from,
          to,
          reason,
          sameDay,
          description,
          counterpartyId
        }) => (
          <PaymentRow
            amount={amount.formattedAmount}
            counterparty={counterparty}
            counterpartyIdFromTransaction={counterpartyId}
            createdAt={createdAt}
            description={description}
            direction={direction}
            estimatedCompletionDate={estimatedCompletionDate}
            from={from}
            id={id}
            key={id}
            reason={reason}
            refetchPayment={refetchPayment}
            sameDay={sameDay}
            status={status}
            summary={summary}
            to={to}
            transactionId={transactionId}
            type={type}
          />
        )
      ),
    [refetchPayment, transactions, counterparty]
  );

  return (
    <Stack
      aria-label="List of payments"
      direction="column"
      paddingBottom="20px"
      role="list"
    >
      <Stack
        direction="column"
        spacing="16px"
      >
        <Text
          color="secondary"
          size="xs"
          uppercase
          weight="medium"
        >
          {title ?? 'All Transactions'}
        </Text>
        <Divider />
        <Stack
          direction="column"
          spacing="8px"
        >
          {paymentRows}
        </Stack>
      </Stack>
      {hasMore && (
        <Stack
          direction="row"
          justifyContent="center"
          pt="40px"
          spacing="8px"
        >
          <LoadingButton
            loading={isLoading}
            onClick={handleLoadMore}
            variant="outlined"
          >
            Load more
          </LoadingButton>
        </Stack>
      )}
    </Stack>
  );
}

ListPaymentTransaction.propTypes = {
  title: PropTypes.string,
  transactions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
      createdAt: PropTypes.string.isRequired,
      summary: PropTypes.string.isRequired,
      amount: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired,
        formattedAmount: PropTypes.string.isRequired
      }).isRequired,
      status: PropTypes.string.isRequired,
      fromAccount: PropTypes.string,
      toAccount: PropTypes.string,
      estimatedCompletionDate: PropTypes.string,
      transactionId: PropTypes.string,
      reason: PropTypes.string,
      sameDay: PropTypes.bool,
      description: PropTypes.string,
      from: PropTypes.shape({
        accountHolder: PropTypes.string,
        routingNumber: PropTypes.string,
        fullAccountNumber: PropTypes.string,
        accountNumber: PropTypes.string
      }),
      to: PropTypes.shape({
        accountHolder: PropTypes.string,
        routingNumber: PropTypes.string,
        fullAccountNumber: PropTypes.string,
        accountNumber: PropTypes.string
      }),
      counterpartyId: PropTypes.string
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  handleLoadMore: PropTypes.func,
  refetchPayment: PropTypes.func.isRequired,
  counterparty: PropTypes.shape({
    counterpartyId: PropTypes.string,
    bank: PropTypes.string,
    connectedType: PropTypes.string,
    accountNumber: PropTypes.string,
    fullAccountNumber: PropTypes.string
  })
};

function areEqual(preProp, nextProp) {
  return (
    preProp.isLoading === nextProp.isLoading &&
    preProp.transactions === nextProp.transactions &&
    preProp.counterparty === nextProp.counterparty
  );
}

export default memo(ListPaymentTransaction, areEqual);
